import type Integration from "@app/types/integrations/Integration";

// Each provider's redirect URI is registered in config and identical across environments, so the
// callback can land on a different origin than the page that started the flow. sessionStorage
// does not cross origins; the OAuth `state` param does.
const REAUTH_STATE_PREFIX = "reauth-";
// Unreserved per RFC 3986, so encodeURIComponent leaves it alone and it reaches the provider
// literally. The point of the choice is not colliding with any provider's own state value.
const STATE_PART_DELIMITER = "~";
const REAUTH_STATE_PATTERN = new RegExp(`^${REAUTH_STATE_PREFIX}(\\d+)$`);
const STATE_PARAM_PATTERN = /([?&]state=)[^&#]*/;

const parseUrl = (url: string): undefined | URL => {
  try {
    return new URL(url);
  } catch {
    // A malformed configured url must not break the redirect; sessionStorage still covers it.
    return undefined;
  }
};

const appendQueryParam = (url: string, param: string): string => {
  const hashIndex = url.indexOf("#");
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? "" : url.slice(hashIndex);
  return `${beforeHash}${beforeHash.includes("?") ? "&" : "?"}${param}${fragment}`;
};

export const withReauthState = (url: string, integrationId: Integration["id"]): string => {
  const parsedUrl = parseUrl(url);
  if (!parsedUrl) {
    return url;
  }
  const reauthPart = `${REAUTH_STATE_PREFIX}${integrationId}`;
  // Parsed above for validation and to read the current value only. Writing `state` back through
  // URLSearchParams would re-serialize the entire query — spaces become `+`, `:` becomes `%3A` —
  // and providers are strict about `redirect_uri`, so edit the raw string and leave the rest of
  // the query byte-identical.
  const existingState = parsedUrl.searchParams.get("state");
  if (existingState === null) {
    return appendQueryParam(url, `state=${encodeURIComponent(reauthPart)}`);
  }
  // Dynamics sends its own state (`dynamics-production`), so append rather than replace.
  const combinedState = `${existingState}${STATE_PART_DELIMITER}${reauthPart}`;
  return url.replace(STATE_PARAM_PATTERN, `$1${encodeURIComponent(combinedState)}`);
};

export const getReauthIntegrationIdFromState = (
  state: string | undefined
): Integration["id"] | undefined => {
  const reauthMatch = state
    ?.split(STATE_PART_DELIMITER)
    .map((part) => REAUTH_STATE_PATTERN.exec(part))
    .find((match) => match !== null);
  if (!reauthMatch) {
    return undefined;
  }
  const integrationId = parseInt(reauthMatch[1], 10);
  return integrationId > 0 ? integrationId : undefined;
};

// Strips our marker so consumers of the provider's own state never see it — onCreateIntegration
// identifies Dynamics by an exact `dynamics-production`. Returns undefined when nothing is left,
// meaning the state carried no provider value of its own.
export const withoutReauthState = (state: string | undefined): string | undefined => {
  const providerParts = state
    ?.split(STATE_PART_DELIMITER)
    .filter((part) => !REAUTH_STATE_PATTERN.test(part));
  return providerParts?.length ? providerParts.join(STATE_PART_DELIMITER) : undefined;
};
